// Michigan Technological University: public college page (#2652, round 2),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Michigan Tech's Common Data Set 2025-2026 (cds-2025-2026.xlsx from the
//    Office of Institutional Research): C1 applicants, admits and enrollees
//    for Fall 2025 with the residency table, C8 test policy for Fall 2027,
//    C21/C22 (answers no to early decision and early action), G1/G5
//    2026-2027 costs.
//  - mtu.edu admissions: First-Year Applicants (apply steps; "2027
//    application is now live!", the Early Action, Regular and Rolling
//    decision timeline, the test score rule) and Admissions Requirements
//    (the programs that ask for more). The Requirements page's test line
//    still names "2025 semesters"; the First-Year page, which names the 2027
//    application, says the same thing (scores optional unless the GPA is
//    below 3.00).
//  - College Scorecard API, school id 171128 (net price by family income).
// Departures: the CDS C22 answers no to early action, but the admissions
// pages for 2027 offer a nonbinding Early Action date (November 1); the
// admissions pages win. Cost: the CDS G1/G5 items are used. The financial
// aid office's 2026-27 table lists $15,321 for housing and food (CDS:
// $14,321), lab and course fees and personal expenses as separate lines,
// and a lower-division tuition column whose totals do not match its own
// items, so it is not used.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Michigan Tech Common Data Set 2025-2026',
        url: 'https://www.mtu.edu/institutional-research/cds/files/cds-2025-2026.xlsx',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Michigan Tech Admissions, First-Year Applicants',
        url: 'https://www.mtu.edu/admissions/apply/steps/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Michigan Tech Admissions, Admissions Requirements',
        url: 'https://www.mtu.edu/admissions/apply/requirements/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?171128-Michigan-Technological-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'michigan-technological-university',
    kbId: 'michigan_technological_university',
    name: 'Michigan Technological University',
    shortName: 'Michigan Tech',
    aliases: ['Michigan Tech', 'MTU'],
    location: 'Houghton, MI',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Michigan Tech visual identity); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FFCD00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/michigan_technological_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 10359,
        admitted: 8853,
        enrolled: 1448,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 6047, admitted: 5210 },
            { group: 'Out-of-state', applied: 3428, admitted: 2798 },
            { group: 'International', applied: 884, admitted: 845 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Michigan Tech\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'The Common Data Set answers no to early decision and early action and gives no early counts. For fall 2027, Michigan Tech\'s admissions pages offer a nonbinding Early Action date; see the deadlines below.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission requirements vary by major, and essays and letters of recommendation are not needed for most. A few programs ask for more:',
        rows: [
            { school: 'Audio Production and Technology, Sound Design, Theatre and Entertainment Technology', requirement: 'Additional application materials may be required.' },
        ],
        source: 'requirements',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 15', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'By March 1', binding: false },
            { plan: 'Rolling Decision', deadline: 'After February 1', decision: 'Within 2 to 3 weeks', binding: false },
        ],
        note: 'Early Action is nonbinding, and its applicants are reviewed first. The enrollment deposit is due May 1 for every plan. You can apply with Michigan Tech\'s own application or the Common App, but submit only one.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Test scores are optional for domestic students: you need to send SAT or ACT scores only if your high school GPA is below 3.00. If you send scores, Michigan Tech uses them only if they strengthen your application. Competitive scholarship applications do not need test scores either.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended for fall 2027 applicants and notes that tests are not required for most students with over a 3.00 high school GPA.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [20396, 46294] },
            { item: 'Required fees', amounts: [382, 382] },
            { item: 'Housing (on campus)', amounts: [8028, 8028] },
            { item: 'Food (on-campus meal plan)', amounts: [6293, 6293] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [1474, 1474] },
            { item: 'Other expenses', amounts: [1372, 1372] },
        ],
        totals: [39145, 65043],
        note: 'For a full-time undergraduate living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 13446,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: 1188 },
                { income: '$48,001 to $75,000', amount: 5282 },
                { income: '$75,001 to $110,000', amount: 12038 },
                { income: 'Over $110,000', amount: 23346 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 band. Your own price comes from Michigan Tech\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
